import { useState, useEffect, useRef, useCallback } from "react";

export const SidebarStatusCard = () => {
  const [now] = useState(() => new Date());
  const hhRef = useRef<HTMLSpanElement>(null);
  const mmRef = useRef<HTMLSpanElement>(null);
  const ssRef = useRef<HTMLSpanElement>(null);

  const tick = useCallback(() => {
    const d = new Date();
    if (hhRef.current)
      hhRef.current.textContent = d.getHours().toString().padStart(2, "0");
    if (mmRef.current)
      mmRef.current.textContent = d.getMinutes().toString().padStart(2, "0");
    if (ssRef.current)
      ssRef.current.textContent = d.getSeconds().toString().padStart(2, "0");
  }, []);

  useEffect(() => {
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tick]);

  const dow = now.getDay();

  const dayName = now.toLocaleDateString("es-CL", { weekday: "long" });
  const dateStr = now.toLocaleDateString("es-CL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const weekDays = ["L", "M", "X", "J", "V"];

  return (
    <div className="relative overflow-hidden rounded-lg border border-global-secondary">
      {/* Subtle accent wash */}
      <div className="absolute inset-0 bg-global-accent/[0.04]" />

      {/* Decorative concentric rings – top right corner */}
      <svg
        className="absolute -top-8 -right-8 w-32 h-32 text-global-accent opacity-[0.07] pointer-events-none"
        viewBox="0 0 128 128"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="96"
          cy="32"
          r="48"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle
          cx="96"
          cy="32"
          r="30"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle
          cx="96"
          cy="32"
          r="14"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle cx="96" cy="32" r="4" fill="currentColor" />
      </svg>

      {/* Accent gradient top bar */}
      <div className="relative h-0.5 bg-gradient-to-r from-global-accent via-global-accent/50 to-transparent" />

      <div className="relative p-3">
        {/* Clock + live dot */}
        <div className="flex items-start justify-between mb-2.5">
          <div>
            <div className="flex items-end gap-0.5 leading-none mb-1">
              <span
                ref={hhRef}
                className="font-black text-global-primary tracking-tighter leading-none"
                style={{ fontSize: "var(--text-4xl)" }}
              />
              <span
                className="font-black text-global-accent leading-none"
                style={{ fontSize: "var(--text-3xl)", marginBottom: "2px" }}
              >
                :
              </span>
              <span
                ref={mmRef}
                className="font-black text-global-primary tracking-tighter leading-none"
                style={{ fontSize: "var(--text-4xl)" }}
              />
              <span
                className="font-black text-global-accent leading-none"
                style={{ fontSize: "var(--text-3xl)", marginBottom: "2px" }}
              >
                :
              </span>
              <span
                ref={ssRef}
                className="font-black text-global-primary tracking-tighter leading-none"
                style={{ fontSize: "var(--text-4xl)" }}
              />
            </div>
            <p className="text-size-2xs text-global-muted capitalize leading-tight">
              {dayName}
            </p>
            <p className="text-size-xs font-medium text-global-secondary capitalize leading-tight">
              {dateStr}
            </p>
          </div>

          <span className="relative flex h-2 w-2 mt-1 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-global-accent opacity-40" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-global-accent" />
          </span>
        </div>

        <div className="h-px bg-global-secondary mb-2.5" />

        {/* Week progress — Mon → Fri */}
        <div className="flex items-end gap-1">
          {weekDays.map((label, i) => {
            const d = i + 1;
            const isToday = d === dow;
            const isPast = dow >= 1 && dow <= 5 && d < dow;
            return (
              <div
                key={label}
                className="flex-1 flex flex-col items-center gap-0.5"
              >
                <div
                  className={`w-full rounded-sm transition-all ${
                    isToday
                      ? "h-2 bg-global-accent"
                      : isPast
                        ? "h-1.5 bg-global-accent/35"
                        : "h-1 bg-global-secondary"
                  }`}
                />
                <span
                  className={`text-size-2xs leading-none ${
                    isToday
                      ? "text-global-accent font-bold"
                      : isPast
                        ? "text-global-tertiary"
                        : "text-global-muted"
                  }`}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
